import { render, h, type ComponentChildren } from 'preact';
import type { CheckoutCompletionResponse, MountCheckoutError, MountCheckoutOptions, MountedCheckout, AccessChangeHandler, AccessResponse, AuthAdapter, CampaignPromo, CheckoutCallbacks, DonationControls, DonationsConfig, EzSubscriptionsApi, EzSubscriptionsReadyCallback, FunnelSurface, OpenDonationOptions, ProductConfig, Purchase, ReturnedTrialSetup, Unsubscribe } from '@/types';
import { fetchProductConfig, fetchProductConfigDetailed, fetchPriceConfigDetailed, fetchDonationsDetailed, getSiteConfig, getSiteConfigDetailed, checkProductAccess, checkItemAccess, fetchHeldProducts, fetchPurchases, establishSession, trackCustomEvent } from '@/api/client';
import { resolveCampaignPromo } from '@/campaignPromo';
import { prewarmStripe } from '@/api/stripe-loader';
import { registerAdapter, getAdapter, validateAuthAdapter } from '@/authAdapter';
import { getIdentityAdapter } from '@/identityAdapter';
import { on as onAccessChange, off as offAccessChange, emit as emitAccessChange } from '@/accessEvents';
import { logError, logWarn } from '@/sdkConsole';
import { t } from '@/i18n/t';
import { applyHostDirection } from '@/i18n/loader';
import { isValidEmail } from '@/email';
import { disableAds, allowAds, acquireInlineAdHold } from '@/adRemoval';
import { signOutEzAuth, ezAuthUrlTokenKind, stripEzAuthUrlToken } from '@/ensureEzAuth';
import { openSubscriberPortal } from '@/portalHandoff';
import { hasSession, getToken, clearSession, beginSessionRequest, getPendingIntent, clearPendingIntent, clearPendingCheckout, getSandboxGateToken, isSandboxMode, type PendingIntent } from '@/session';
import { RESUME_PARAM, isSecureContext, parseResumeParam, stripResumeParam, type SecureResumePayload } from '@/secureRedirect';
import { readSetupIntentReturn, stripSetupIntentReturn } from '@/setupIntentReturn';
import { CheckoutPresentationProvider } from '@/components/CheckoutPresentation';
import inlineStyles from '@/inlineCheckout.css?inline';
import { Paywall } from '@/paywall';
import { DonationLauncher } from '@/components/DonationLauncher';
import { LoginFlow } from '@/components/LoginFlow';
import { CheckoutShell } from '@/components/CheckoutShell';
import { ErrorStep } from '@/components/ErrorStep';
import { SuccessStep } from '@/components/SuccessStep';
import { LocalDevStep } from '@/components/LocalDevStep';
import { isLocalHostname, warnLocalDevTenant } from '@/localDev';
import { runHook } from '@/components/runHook';
import { currentPageItem, isItemScoped, resolveItemForPrice } from '@/page';
import { whenBodyReady } from '@/bodyReady';
import { applyTheme } from '@/theme';
import { parkedDialogAnchor, setDialogAnchor, type DialogAnchor } from '@/dialogAnchor';
import styles from '@/styles.css?inline';

// A publisher-supplied prefill email (showPaywall/openCheckout { email }) is
// honored only when well-formed; an invalid one is dropped with a warning
// rather than prefilling a form the visitor must correct. The server remains
// the authority on the email either way.
function prefillEmail(email?: string): string | undefined {
  const trimmed = email?.trim() || undefined;
  if (trimmed && !isValidEmail(trimmed)) {
    logWarn('ignoring invalid email option (not a valid email address)', trimmed);
    return undefined;
  }
  return trimmed;
}

// onSuccess is deprecated on entitlement checkouts: the hook is a closure on
// this page's checkout instance, so a full-page redirect (3DS, BYO
// navigate-away login) drops it. openDonation is exempt — donations grant no
// entitlement, so the access:change a donation emits cannot identify it.
function warnOnSuccessDeprecated(method: string, opts?: CheckoutCallbacks): void {
  if (typeof opts?.onSuccess === 'function') {
    logWarn(`${method}: onSuccess is deprecated and does not survive page redirects (3DS, login); use on('access:change') + hasAccess/hasPurchased for anything that must happen when access is granted`);
  }
}

// installSdk publishes the publisher-facing window.ezsubscriptions API.
// bootstrapDone resolves once invisible session restoration (magic-link
// exchange, pending checkout resume) has finished, so access checks see the
// restored session.
// returned resolves with bootstrap, to a checkout that completed on this load
// (payment redirect or reload) or null.
export function installSdk(bootstrapDone: Promise<void>, returned: Promise<CheckoutCompletionResponse | null>): EzSubscriptionsApi {
  const api = createApi(bootstrapDone, returned);

  // Adopt callbacks queued on cmd before the script loaded (Google Publisher Tag
  // pattern), then make cmd.push run callbacks immediately so post-load pushes
  // behave the same as pre-load ones.
  const queued = window.ezsubscriptions?.cmd ?? [];
  api.cmd.push = ((cb: EzSubscriptionsReadyCallback) => {
    runReadyCallback(api, cb);
    return api.cmd.length;
  }) as typeof api.cmd.push;
  window.ezsubscriptions = api;
  for (const cb of queued) {
    runReadyCallback(api, cb);
  }

  registerDeclarativeAuth(api);
  registerDeclarativeDonation(api);

  return api;
}

const LOGIN_ATTR = 'data-ezoic-login';
const LOGOUT_ATTR = 'data-ezoic-logout';
const DONATE_ATTR = 'data-ezoic-donate';
const DONATE_PRODUCT_ATTR = 'data-ezoic-product-id';
const DONATE_AMOUNT_ATTR = 'data-ezoic-amount-cents';

// registerDeclarativeAuth lets a publisher open login or trigger logout with
// markup alone — e.g. <a data-ezoic-login>Log in</a> / <a data-ezoic-logout>Log
// out</a> in their nav — with no script. Delegated from document (publisher
// triggers live in the light DOM, not the widget's shadow root), so it also
// covers triggers injected after load.
function registerDeclarativeAuth(api: EzSubscriptionsApi): void {
  const onClick = (event: MouseEvent) => {
    const target = event.target as Element | null;
    if (target?.closest?.(`[${LOGOUT_ATTR}]`)) {
      event.preventDefault();
      void api.logout();
      return;
    }
    if (target?.closest?.(`[${LOGIN_ATTR}]`)) {
      event.preventDefault();
      void api.login().catch((err) => logWarn('login could not open', err));
    }
  };
  document.addEventListener('click', onClick);
}

// registerDeclarativeDonation lets a publisher open the donation dialog with
// markup alone — <button data-ezoic-donate>Support us</button>, optionally with
// data-ezoic-product-id / data-ezoic-amount-cents — with no script. Delegated
// from document (always on, so declarative triggers work without a setup call),
// which also covers triggers injected after load. openDonation self-mounts the
// launcher on the first click.
function registerDeclarativeDonation(api: EzSubscriptionsApi): void {
  const onClick = (event: MouseEvent) => {
    const target = event.target as Element | null;
    const trigger = target?.closest?.(`[${DONATE_ATTR}]`);
    if (!trigger) {
      return;
    }
    event.preventDefault();
    void api.openDonation({
      productId: trigger.getAttribute(DONATE_PRODUCT_ATTR) ?? undefined,
      amountCents: parseAmountCents(trigger.getAttribute(DONATE_AMOUNT_ATTR)),
    }).catch((err) => logWarn('donation could not open', err));
  };
  document.addEventListener('click', onClick);
}

// parseAmountCents reads a data-ezoic-amount-cents attribute into a positive
// integer cents value, or undefined when absent/invalid (the picker then shows
// its default presets).
function parseAmountCents(raw: string | null): number | undefined {
  if (raw == null) {
    return undefined;
  }
  const cents = Number(raw.trim());
  return Number.isInteger(cents) && cents > 0 ? cents : undefined;
}

function createApi(bootstrapDone: Promise<void>, returned: Promise<CheckoutCompletionResponse | null>): EzSubscriptionsApi {
  // prewarm starts the Stripe.js download the moment a checkout surface opens
  // so the payment form's mount-time load is a cache hit. Chained on
  // bootstrapDone because prewarm reads the memoized site-config, and sandbox
  // activation (which re-points the API base) must win that first fetch.
  const prewarm = (): void => {
    void bootstrapDone.then(prewarmStripe).catch((err) => logWarn('Stripe.js prewarm failed', err));
  };

  const hasAccess = async (query: string | { product: string }): Promise<AccessResponse> => {
    await bootstrapDone;
    // Anonymous visitors hold no products; skip the round trip.
    if (!hasSession()) {
      return { decision: 'login_required', reasonCode: 'customer_required' };
    }
    const product = (typeof query === 'string' ? query : query?.product)?.trim();
    if (!product) {
      logError('hasAccess requires a product', query);
      return { decision: 'denied', reasonCode: 'no_entitlement' };
    }
    const result = await checkProductAccess(product);
    if (result.decision === 'unknown_product') {
      logWarn('hasAccess: product not recognized (check the product handle)', product);
    }
    return result;
  };

  const hasPurchased = async (query: { item: string } | { page: true }): Promise<AccessResponse> => {
    await bootstrapDone;
    // Anonymous visitors hold no purchases; skip the round trip.
    if (!hasSession()) {
      return { decision: 'login_required', reasonCode: 'customer_required' };
    }
    // { page: true } checks the current page (a "current article automatically"
    // price): the widget resolves the same normalized page key it stamped at
    // checkout, so the publisher never handles it. Otherwise the publisher names
    // the item.
    const item = 'page' in query ? currentPageItem() : query?.item?.trim();
    if (!item) {
      // No item key to match means nothing is held; treat it as a denial
      // rather than a server round trip, mirroring how an unknown item is denied.
      logError('hasPurchased requires { item } or { page: true }', query);
      return { decision: 'denied', reasonCode: 'no_entitlement' };
    }
    return checkItemAccess(item);
  };

  const getProducts = async (): Promise<string[]> => {
    await bootstrapDone;
    // Anonymous visitors hold no products; skip the round trip.
    if (!hasSession()) {
      return [];
    }
    return fetchHeldProducts();
  };

  const getPurchases = async (): Promise<Purchase[]> => {
    await bootstrapDone;
    // Anonymous visitors hold no purchases; skip the round trip.
    if (!hasSession()) {
      return [];
    }
    return fetchPurchases();
  };
  // Parked secure-checkout resume payload (see resumeSecureRedirect), merged
  // into matching mountPaywall calls until dismissed or completed. createApi
  // scope so both see it.
  let pendingSecureResume: SecureResumePayload | null = null;
  // The trial 3DS-return resume (see resumeTrialSetupReturn), merged into
  // matching mounts on this load the same way, so a publisher's pop-on-load
  // remount can't drop it. Cleared once a flow takes it for its confirm, or on
  // dismiss/success of a mount that carried it.
  let pendingTrialResume: { intent: PendingIntent; setup: ReturnedTrialSetup } | null = null;
  const clearTrialResume = <A extends unknown[]>(fn: ((...args: A) => void) | undefined) => (...args: A) => {
    pendingTrialResume = null;
    fn?.(...args);
  };
  let mountedCheckout: (MountedCheckout & { isActive(): boolean }) | null = null;
  let modalPending = 0;
  let modalPaywallOpen = false;

  const hasMountedCheckout = (): boolean => {
    if (mountedCheckout && !mountedCheckout.isActive()) {
      mountedCheckout.destroy();
    }
    return mountedCheckout !== null;
  };

  const rejectWhileMounted = (): void => {
    if (hasMountedCheckout()) {
      throw mountCheckoutError('checkout_active', 'A mounted checkout is active; call destroy() before opening another checkout.');
    }
  };

  // Every modal open sets the anchor, so one left by an earlier call never
  // carries into a donation or a secure-resume checkout. Opens that bypass
  // runModal (resume mountPaywall, URL-token login) set it themselves.
  const runModal = async (open: () => Promise<void>, anchor?: DialogAnchor): Promise<void> => {
    rejectWhileMounted();
    setDialogAnchor(anchor);
    modalPending += 1;
    try {
      await open();
    } finally {
      modalPending -= 1;
    }
  };

  const mountPaywall = (config: ProductConfig, opts: { product: string; item?: string; dismissible?: boolean; email?: string; campaignPromo?: CampaignPromo | null; resumePriceExternalId?: string; resumeAmountCents?: number; resumeTrial?: boolean; trialSetupResume?: ReturnedTrialSetup; onTrialSetupResumeConsumed?: () => void; surface?: FunnelSurface; resumed?: boolean; inline?: InlineCheckout } & CheckoutCallbacks): void => {
    if (opts.inline && !opts.inline.isActive()) {
      return;
    }
    if (!opts.inline) {
      rejectWhileMounted();
    }
    if (tokenLandingOverlayOpen()) {
      logWarn('suppressing paywall mount while the account-link landing overlay is open', opts.product);
      return;
    }
    if (!config.product) {
      // The backend returns no product for both an unknown product handle and a
      // product with no active prices; it cannot distinguish them, so name both.
      // Reachable from showPaywall and the post-login resume, so the message
      // names the product rather than a specific call. Logged as an error (not
      // a warning) because this is a permanent dead end for the call — nothing
      // will ever render until the publisher fixes the product config.
      logError('product has no active prices; check the product handle exists and has at least one active price', opts.product);
      return;
    }
    // A parked secure-checkout resume for this product merges into EVERY mount
    // that matches it until the visitor dismisses (or completes) a mount that
    // carried it — whoever mounts (the resume dispatch below, or a publisher's
    // pop-on-load showPaywall that was already in flight when the https page
    // loaded). Applying it to every matching mount, rather than consuming it on
    // the first, is what makes the resume survive pop-on-load sites
    // deterministically: the publisher's mount lands last and would otherwise
    // clobber the resumed preselection. Explicit opts always win over the
    // parked values.
    // A parked product-kind resume merges into a matching paywall mount. It must
    // NOT merge into a direct-checkout (openCheckout) mount for the same product:
    // that mount has its own preselected price, and the merge would force
    // resumed=true on a genuinely fresh openCheckout, suppressing its
    // price_selected. Only paywall-surface mounts (surface unset) participate.
    if (pendingSecureResume && pendingSecureResume.kind === 'product' && pendingSecureResume.product === opts.product && opts.surface === undefined) {
      const parked = pendingSecureResume;
      // dismissible is not merged: every caller defaults it (!== false) before
      // mountPaywall, so the explicit value always wins by construction.
      opts = {
        ...opts,
        item: opts.item ?? parked.item,
        // The parked campaign code rides along unless the mount names its own.
        campaignPromo: opts.campaignPromo ?? parked.promo ?? null,
      };
      // Merge the selection as a unit: a mount with its own explicit
      // resumePriceExternalId (e.g. a BYO pending-intent resume) must not pick
      // up the parked trial/amountCents flags, which belong to the parked price.
      if (opts.resumePriceExternalId === undefined) {
        opts.resumePriceExternalId = parked.priceExternalId;
        opts.resumeAmountCents = parked.amountCents;
        opts.resumeTrial = parked.trial;
      }
      // A mount carrying a parked selection is a resume: it must not re-emit
      // price_selected for a selection counted before the redirect.
      opts.resumed = true;
      // The park ends once the visitor has seen and dismissed (or completed) a
      // paywall that carried it — a later showPaywall then opens fresh at the
      // plan picker instead of silently re-applying the parked selection. (A
      // mount that self-suppresses because the visitor is already entitled
      // never fires either hook, so the park can linger for the rest of the
      // load in that case — harmless: later mounts self-suppress identically.)
      const clearPark = <A extends unknown[]>(fn: ((...args: A) => void) | undefined) => (...args: A) => {
        pendingSecureResume = null;
        fn?.(...args);
      };
      opts = { ...opts, onCancel: clearPark(opts.onCancel), onSuccess: clearPark(opts.onSuccess) };
    }
    // A trial 3DS-return resume for this product merges the same way, unless
    // the mount names a different price of its own.
    const trialResume = pendingTrialResume;
    if (trialResume && trialResume.intent.kind === 'product' && trialResume.intent.product === opts.product && opts.surface === undefined && (opts.resumePriceExternalId === undefined || opts.resumePriceExternalId === trialResume.intent.priceExternalId)) {
      opts = {
        ...opts,
        item: opts.item ?? trialResume.intent.item,
        campaignPromo: opts.campaignPromo ?? trialResume.intent.promo ?? null,
        resumePriceExternalId: trialResume.intent.priceExternalId,
        resumeAmountCents: undefined,
        resumeTrial: true,
        resumed: true,
        trialSetupResume: trialResume.setup,
        onCancel: clearTrialResume(opts.onCancel),
        onSuccess: clearTrialResume(opts.onSuccess),
      };
    }
    if (opts.trialSetupResume) {
      opts.onTrialSetupResumeConsumed = () => {
        pendingTrialResume = null;
      };
    }
    applyTheme(opts.inline?.host ?? shadowHostElement(), config.product.theme);
    const point = opts.inline?.point ?? mountPoint('paywall');
    // A previous showPaywall may have left a dismissed (or unlocked) Paywall
    // instance mounted but rendering nothing. Re-rendering the same component
    // into the same container makes Preact reuse that instance and its stale
    // internal state, so the wall would silently stay closed — exactly the
    // "close the paywall, read on, click unlock again, nothing happens until a
    // page refresh" case. Unmount first so every call mounts a fresh paywall.
    render(null, point);
    const wall = h(Paywall, {
      config,
      productKey: opts.product,
      item: opts.item,
      dismissible: opts.dismissible,
      campaignPromo: opts.campaignPromo,
      initialPriceExternalId: opts.resumePriceExternalId,
      initialAmountCents: opts.resumeAmountCents,
      initialTrial: opts.resumeTrial,
      trialSetupResume: opts.trialSetupResume,
      onTrialSetupResumeConsumed: opts.onTrialSetupResumeConsumed,
      initialEmail: opts.email,
      surface: opts.surface,
      resumed: opts.resumed,
      onSuccess: opts.onSuccess,
      onCancel: opts.onCancel,
      onError: opts.onError,
      onHidden: opts.inline ? undefined : () => { modalPaywallOpen = false; },
    });
    if (!opts.inline) {
      modalPaywallOpen = true;
    }
    render(opts.inline ? inlinePresentation(wall, opts.inline) : wall, point);
  };

  // mountError shows the shared failure surface in the paywall point when
  // showPaywall can't even start — the product config fails to load — so the
  // visitor sees a friendly, dismissible explanation instead of a silently empty
  // page (the reason was previously only in the console). The publisher's onError
  // hook fires too so the failure is observable.
  const mountError = (message: string, opts?: CheckoutCallbacks): void => {
    const point = mountPoint('paywall');
    render(null, point);
    render(h(ErrorStep, {
      message,
      onDismiss: () => render(null, point),
    }), point);
    runHook(opts?.onError, 'onError', { message });
  };

  // showReturnedPurchase confirms a checkout that completed after a payment
  // redirect or reload. It has its own mount point: a paywall the publisher
  // pops on load for the product just bought unlocks and shows nothing, so the
  // confirmation must not share (or wait on) the paywall point.
  const showReturnedPurchase = async (completion: CheckoutCompletionResponse): Promise<void> => {
    await whenBodyReady();
    setDialogAnchor(null);
    applyTheme(shadowHostElement(), undefined);
    const point = mountPoint('return');
    const close = () => render(null, point);
    render(null, point);
    render(h(SuccessStep, { priceType: completion.priceType, maskedEmail: completion.maskedEmail, portalUrl: completion.portalUrl, onClose: close, onDismiss: close }), point);
  };
  returned.then((completion) => {
    if (completion) {
      return showReturnedPurchase(completion);
    }
    return undefined;
  }).catch((err) => logError('checkout return success could not show', err));

  // mountLocalDev shows the local-development guidance surface when a
  // tenant-scoped config fetch fails on a local origin (localhost / loopback):
  // the page hostname can never resolve a site, so the only audience is the
  // integrating developer. notSetup narrows the failure to "the alias resolved
  // but the domain has never begun SM setup", which gets its own copy. Fires
  // the onError hook like mountError so the failure stays observable.
  const mountLocalDev = (opts?: CheckoutCallbacks, notSetup?: boolean): void => {
    warnLocalDevTenant(notSetup);
    const point = mountPoint('paywall');
    render(null, point);
    render(h(LocalDevStep, {
      host: window.location.hostname,
      port: window.location.port,
      notSetup,
      onDismiss: () => render(null, point),
    }), point);
    runHook(opts?.onError, 'onError', { message: notSetup ? 'domain is not set up for Subscriptions' : 'local origin cannot resolve a site' });
  };

  const showPaywallFlow = async (opts?: { product?: string; offer?: string; item?: string; dismissible?: boolean; email?: string; promoCode?: string } & CheckoutCallbacks): Promise<void> => {
    prewarm();
    await bootstrapDone;
    // Pop-on-load callers can reach the mount before the parser has produced
    // <body> (async script, cached copy); mounting needs it.
    await whenBodyReady();
    warnOnSuccessDeprecated('showPaywall', opts);
    const product = paywallProductHandle(opts);
    if (!product) {
      logError('showPaywall requires a product');
      return;
    }
    // The mount's campaign code: an explicit promoCode option wins, then a
    // parked secure-resume's code (the https landing has no URL stash —
    // sessionStorage is origin-scoped — so without this a pop-on-load mount
    // would fetch the config codeless and the picker would lose the parked
    // discounts the mountPaywall merge later restores), then the URL stash.
    let campaignPromo = resolveCampaignPromo(opts?.promoCode);
    if (!opts?.promoCode && pendingSecureResume?.kind === 'product' && pendingSecureResume.product === product && pendingSecureResume.promo) {
      campaignPromo = pendingSecureResume.promo;
    }
    const { config, domainNotFound, domainNotSetup, domainDisabled } = await fetchProductConfigDetailed(product, campaignPromo?.code);
    if (!config) {
      // Local-dev guidance only when the backend answered a tenant 404 on
      // a local origin — a network/5xx failure (e.g. the dev's local backend
      // is down) is not a tenant problem and gets the generic load failure.
      if (domainNotFound && isLocalHostname(window.location.hostname)) {
        mountLocalDev(opts, domainNotSetup);
        return;
      }
      // A tenant 403/404 is permanent — the visitor cannot fix it. The client
      // already logged the status; render nothing, same as a bad handle. Only
      // a real load failure (transport, 5xx, !success) shows the error modal.
      if (domainNotFound || domainDisabled) {
        return;
      }
      // The product config could not be loaded (network/server error); surface it
      // rather than leaving the visitor staring at a page that did nothing.
      logError('showPaywall could not load product config', product);
      mountError(t('checkout.loadFailed'), opts);
      return;
    }
    mountPaywall(config, {
      product,
      item: opts?.item?.trim() || undefined,
      dismissible: opts?.dismissible !== false,
      email: prefillEmail(opts?.email),
      campaignPromo,
      onSuccess: opts?.onSuccess,
      onCancel: opts?.onCancel,
      onError: opts?.onError,
    });
  };

  // `resumed` is internal (absent from the public ApiSurface type): set by the
  // resume paths below, which re-open the same checkout after a BYO login
  // navigate-away or an http→https redirect. It suppresses the duplicate
  // price_selected on the remount — the selection was already counted before
  // the reload — mirroring how mountPaywall's resumePriceExternalId auto-select
  // skips the emit in CheckoutFlow.
  // `campaignPromo` is internal (absent from the public EzSubscriptionsApi
  // type, same as `resumed`): the resume transports pass the parked provenance
  // through; public callers use promoCode.
  // `trialSetupResume` is internal too: set only by resumeTrialSetupReturn.
  const openCheckoutFlow = async (opts: { price: string; item?: string; dismissible?: boolean; email?: string; promoCode?: string; resumed?: boolean; campaignPromo?: CampaignPromo | null; trialSetupResume?: ReturnedTrialSetup } & CheckoutCallbacks, inline?: InlineCheckout): Promise<void> => {
    prewarm();
    await bootstrapDone;
    await whenBodyReady();
    if (inline && !inline.isActive()) {
      return;
    }
    warnOnSuccessDeprecated('openCheckout', opts);
    const price = opts?.price?.trim();
    if (!price) {
      if (inline) {
        throw new Error(t('inline.configFailed.permanent'));
      }
      logError('openCheckout requires a price');
      return;
    }
    // A trial 3DS-return resume for this price merges into a modal mount the
    // same way mountPaywall merges a product resume.
    const trialResume = pendingTrialResume;
    if (!inline && trialResume && (trialResume.intent.kind === 'checkout' || trialResume.intent.kind === 'item') && trialResume.intent.price === price) {
      opts = {
        ...opts,
        item: opts.item ?? trialResume.intent.item,
        campaignPromo: opts.campaignPromo !== undefined ? opts.campaignPromo : trialResume.intent.promo,
        resumed: true,
        trialSetupResume: trialResume.setup,
        onCancel: clearTrialResume(opts.onCancel),
        onSuccess: clearTrialResume(opts.onSuccess),
      };
    }
    const item = opts?.item?.trim();
    // The mount's campaign code (see showPaywall). A resume passes the parked
    // one verbatim; a fresh call resolves option-over-URL-stash.
    const campaignPromo = opts?.campaignPromo !== undefined ? opts.campaignPromo : resolveCampaignPromo(opts?.promoCode);
    // The price-config resolves the price key to its parent product and carries
    // the domain fields (authMode, domainId, guest/expedited, supportEmail,
    // checkoutText) the checkout gate needs. The site-config is fetched alongside
    // only to detect a total site-config outage (a BYO domain's checkout gate
    // can't apply without it) — the price-config is the mount's ProductConfig.
    const [config, priceResult] = await Promise.all([getSiteConfig(), fetchPriceConfigDetailed(price, campaignPromo?.code)]);
    if (inline && !inline.isActive()) {
      return;
    }
    const priceConfig = priceResult.config;
    if (!config) {
      // Proceed rather than block, but a BYO domain's checkout gate can't apply
      // and magic-link login is disabled without the domainId; make that visible.
      logWarn('openCheckout could not load site configuration; proceeding without auth-mode gating');
    }
    if (inline && (priceResult.domainNotFound || priceResult.domainDisabled)) {
      throw new Error(t('inline.domainUnavailable'));
    }
    if (priceResult.domainNotFound && isLocalHostname(window.location.hostname)) {
      // The backend answered a tenant 404 on a local origin: the page
      // hostname can never resolve a site, so the purchase flow would mount
      // with no domainId and fail later — show the local-dev guidance instead.
      // A network/5xx failure is not a tenant problem and proceeds as before.
      mountLocalDev(opts, priceResult.domainNotSetup);
      return;
    }
    // A tenant 403/404 is permanent — the visitor cannot fix it. The client
    // already logged the status; render nothing, same as a bad handle.
    if (priceResult.domainNotFound || priceResult.domainDisabled) {
      return;
    }
    if (tokenLandingOverlayOpen()) {
      if (inline) {
        throw new Error(t('inline.configFailed.transient'));
      }
      logWarn('suppressing purchase mount while the account-link landing overlay is open', price);
      return;
    }
    // A price config with no product is a resolved domain with nothing to
    // sell (unknown price, invalid key, or inactive parent). Render nothing
    // and log the price — the visitor cannot fix it. A null config (transport,
    // 5xx, !success) is retried once below, then surfaces the error step.
    if (priceConfig && !priceConfig.product) {
      if (inline) {
        throw new Error(t('inline.configFailed.permanent'));
      }
      logError('price has no active prices; check the price handle exists and has at least one active price', price);
      return;
    }
    // A null price-config is a transient load failure (transport/5xx). Today the
    // flow mounts and retries the fetch on mount; preserve that retry by
    // refetching once here (the memoized promise was deleted on failure, so this
    // is a real retry). If it still fails, render nothing — without a resolved
    // product there is no price to preselect and no checkout to start.
    const resolvedConfig = priceConfig ?? (await fetchPriceConfigDetailed(price, campaignPromo?.code)).config;
    if (inline && !inline.isActive()) {
      return;
    }
    const product = resolvedConfig?.product;
    // Theme from the price's parent product; an unknown price or failed load
    // yields undefined, which resets the shared host to classic/light so a
    // previously mounted paywall's theme can't bleed into this surface.
    applyTheme(inline?.host ?? shadowHostElement(), product?.theme);
    if (!resolvedConfig || !product) {
      if (inline) {
        throw new Error(t('inline.configFailed.transient'));
      }
      logError('openCheckout could not load price configuration', price);
      mountError(t('checkout.loadFailed'), opts);
      return;
    }
    const resolvedPrice = product.prices[0];
    if (inline && !resolvedPrice) {
      throw new Error(t('inline.configFailed.permanent'));
    }
    // Enforce the price's grant scope against a publisher-supplied item, failing
    // fast with a console error rather than opening a misconfigured session —
    // the documented openCheckout contract. A product-scoped price rejects a
    // stray item; an item-scoped price requires one (runtime source) that
    // resolves. CheckoutFlow's beginCheckout would otherwise drop a product-
    // scoped price's item client-side (resolveItemForPrice), silently hiding
    // the misconfiguration; the backend re-checks the scope regardless.
    if (resolvedPrice) {
      if (!isItemScoped(resolvedPrice) && item) {
        if (inline) {
          throw new Error(t('inline.configFailed.permanent'));
        }
        logError('openCheckout: this price does not accept an item', price);
        runHook(opts.onError, 'onError', { message: 'This price does not accept an item' });
        return;
      }
      if (isItemScoped(resolvedPrice) && !resolveItemForPrice(resolvedPrice, item)) {
        if (inline) {
          throw new Error(t('inline.configFailed.permanent'));
        }
        logError('openCheckout: this price requires an item', price);
        runHook(opts.onError, 'onError', { message: 'This price requires an item' });
        return;
      }
    }
    // Route openCheckout through the same orchestrator as showPaywall, with the
    // publisher-chosen price preselected. This is what makes a trial-bearing
    // price honor its trial: CheckoutFlow routes trial-eligible prices through
    // the SetupIntent trial rail (handleStartTrial → startTrial → confirmTrial),
    // which ItemPurchaseFlow never did. surface 'checkout' keeps the funnel
    // attribution; resumed threads the fresh-vs-resume distinction. The
    // price-config response already carries the domain fields (domainId,
    // authMode, guest/expedited, supportEmail, checkoutText), so it is the
    // ProductConfig mountPaywall expects.
    mountPaywall(resolvedConfig, {
      product: product.productKey,
      item,
      dismissible: !inline && opts.dismissible !== false,
      email: prefillEmail(opts.email),
      campaignPromo,
      resumePriceExternalId: resolvedPrice?.externalId,
      resumeTrial: (resolvedPrice?.trialEligible && (resolvedPrice?.trialPeriodDays ?? 0) > 0) || undefined,
      trialSetupResume: opts.trialSetupResume,
      surface: 'checkout',
      resumed: opts.resumed,
      onSuccess: opts.onSuccess,
      onCancel: opts.onCancel,
      onError: opts.onError,
      inline,
    });
  };

  const showPaywall: EzSubscriptionsApi['showPaywall'] = (opts) => runModal(() => showPaywallFlow(opts), opts?.anchor);
  const openCheckout: EzSubscriptionsApi['openCheckout'] = (opts) => runModal(() => openCheckoutFlow(opts), opts?.anchor);

  const mountCheckout = async (opts: MountCheckoutOptions): Promise<MountedCheckout> => {
    const target = resolveCheckoutTarget(opts?.target);
    if (typeof opts.price !== 'string' || !opts.price.trim()) {
      throw mountCheckoutError('invalid_price', 'mountCheckout price must be a non-empty string.');
    }
    const successUrl = resolveSuccessUrl(opts.successUrl);
    rejectWhileMounted();
    if (modalPending > 0 || modalPaywallOpen || Object.values(mountPoints).some((point) => point?.querySelector('[role="dialog"]'))) {
      throw mountCheckoutError('checkout_active', 'A modal checkout is active; close it before mounting checkout.');
    }
    const host = document.createElement('div');
    host.setAttribute('data-ezoic-sm-checkout', '');
    applyHostDirection(host);
    const root = host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = styles + inlineStyles;
    const point = document.createElement('div');
    point.className = 'sm-root';
    root.append(style, point);
    target.appendChild(host);
    const releaseAds = acquireInlineAdHold();
    let destroyed = false;
    const handle = {
      isActive: () => !destroyed && host.isConnected,
      destroy() {
        if (destroyed) {
          return;
        }
        destroyed = true;
        render(null, point);
        host.remove();
        releaseAds();
        if (mountedCheckout === handle) {
          mountedCheckout = null;
        }
      },
    };
    mountedCheckout = handle;
    const isActive = () => {
      if (!host.isConnected) {
        handle.destroy();
      }
      return handle.isActive();
    };
    const inline = { host, point, successUrl, isActive: handle.isActive };
    const onError: MountCheckoutOptions['onError'] = (error) => {
      if (isActive()) {
        runHook(opts.onError, 'onError', error);
      }
    };
    const label = t('inline.checkingAccess');
    render(inlinePresentation(h(CheckoutShell, {
      ariaLabel: label, currentStep: 1, hideSteps: true,
      children: h('p', { role: 'status' }, label),
    }), inline), point);
    void openCheckoutFlow({
      price: opts.price, item: opts.item, email: opts.email,
      promoCode: opts.promoCode, onError,
    }, inline).catch((err) => {
      if (!isActive()) {
        return;
      }
      const message = err instanceof Error ? err.message : t('checkout.errorGeneric');
      render(inlinePresentation(h(ErrorStep, { message }), inline), point);
      onError({ message });
    }).finally(() => {
      if (!host.isConnected) {
        handle.destroy();
      }
    });
    return handle;
  };

  // login opens a standalone login surface for a returning subscriber, off the
  // publisher's own nav (no paywall first). It is mode-aware: ezAuth (and the
  // default) renders the Ezoic login screen; BYO forwards to the publisher's own
  // goToLogin(), since the publisher owns auth there. A login that establishes a
  // session fires access:change (inside LoginFlow) so gated content unlocks in
  // place. Available declaratively too, via a [data-ezoic-login] element.
  const loginFlow = async (opts?: { dismissible?: boolean; onClosed?: () => void }): Promise<void> => {
    await bootstrapDone;
    await whenBodyReady();
    // Already signed in: nothing to log into; re-announce access so any gated UI
    // re-checks, then stop. An ezAuth account-link token in the URL (password
    // reset / email verification) bypasses the no-op: the visitor clicked an
    // emailed link and needs the mounted form's token screen, not a session
    // re-announce.
    if (hasSession() && ezAuthUrlTokenKind() === null) {
      logWarn('login: no-op — visitor already has a session; re-announcing access');
      emitAccessChange();
      return;
    }
    const siteResult = await getSiteConfigDetailed();
    const config = siteResult.config;
    // Same tenant-404 gate as the checkout entry points: on a local origin the
    // only audience is the developer, so show the guidance modal instead of a
    // console error. A transport/5xx failure is not a tenant problem and falls
    // through to the generic config-missing log below.
    if (!config && siteResult.domainNotFound && isLocalHostname(window.location.hostname)) {
      mountLocalDev(undefined, siteResult.domainNotSetup);
      return;
    }
    // BYO: the publisher owns login. Forward to their goToLogin() (navigate-away);
    // validateAuthAdapter logs once when no adapter is wired, and we no-op.
    if (config?.authMode === 'byo') {
      if (!validateAuthAdapter('byo')) {
        return;
      }
      getIdentityAdapter('byo', config.domainId).promptLogin();
      return;
    }
    if (typeof config?.domainId !== 'number') {
      logError('login could not load site configuration');
      return;
    }
    // The standalone login surface is not product-themed; reset the shared host
    // so a previously mounted paywall's theme can't bleed into it.
    applyTheme(shadowHostElement(), undefined);
    const point = mountPoint('login');
    // Unmount any prior instance first so a dismissed-then-reopened login mounts
    // fresh, matching mountPaywall's reasoning.
    render(null, point);
    render(h(LoginFlow, {
      domainId: config.domainId,
      authMode: config.authMode,
      supportEmail: config.supportEmail,
      dismissible: opts?.dismissible !== false,
      // onClosed is internal-only (absent from the public ApiSurface type, same
      // as openCheckout's resumed): the token-landing auto-open uses it to strip
      // the URL token when the overlay is dismissed.
      onClose: () => {
        render(null, point);
        opts?.onClosed?.();
      },
    }), point);
  };

  const login = (opts?: { dismissible?: boolean; onClosed?: () => void; anchor?: DialogAnchor }): Promise<void> => runModal(() => loginFlow(opts), opts?.anchor);

  // logout ends the visitor's content session — the counterpart to login(). It
  // drops the SM content session, removes the ad-free cookie so ads return,
  // discards any parked checkout, and fires access:change so gated content
  // re-locks in place without a reload. The local teardown runs first so a slow
  // ezAuth revoke can never delay the visitor's sign-out. On an ezAuth domain the
  // visitor's ezAuth session must also end, or the surviving refresh token
  // silently re-establishes the SM session on the next load; a returning member's
  // page load skips ezAuth init (an SM session already existed), so signOutEzAuth
  // loads the facade for the domain before revoking. In BYO the publisher owns
  // sign-out of their own login system (the SDK cannot); calling this clears the
  // content session.
  const logout = async (): Promise<void> => {
    await bootstrapDone;
    clearSession();
    clearPendingIntent();
    clearPendingCheckout();
    pendingSecureResume = null;
    pendingTrialResume = null;
    allowAds();
    emitAccessChange();
    const config = await getSiteConfig();
    if (config?.authMode === 'ezauth' && typeof config.domainId === 'number') {
      await signOutEzAuth(config.domainId);
    }
  };

  // getSessionToken returns the signed-in visitor's session JWT, or null when
  // there is no active session (anonymous or expired). It's the client half of
  // the server-to-server REST API's reader-token path: a publisher whose server
  // doesn't know the reader's email (Ezoic-visitor-account sites) reads this on
  // the page, relays it to their backend, and forwards it as the
  // X-Ezoic-Reader-Token header. The token is domain-scoped and short-lived, so
  // fetch it per request rather than caching it.
  const getSessionToken = (): string | null => getToken();

  // getSandboxPreviewToken returns the sandbox gate (preview) token while the
  // publisher previews the sandbox, else null. For the WordPress plugin, which
  // forwards it so sandbox SM accepts its reader calls during a preview. Never
  // the visitor session. Awaits bootstrap so a fragment handoff has landed.
  const getSandboxPreviewToken = async (): Promise<string | null> => {
    await bootstrapDone;
    return isSandboxMode() ? getSandboxGateToken() : null;
  };

  const mountDonations = (config: DonationsConfig): void => {
    if (config.donations.length === 0) {
      return;
    }
    // The donations overlay is one shell for several products; the first donation
    // product's theme drives it (see research doc).
    applyTheme(shadowHostElement(), config.donations[0]?.theme);
    render(h(DonationLauncher, {
      domainId: config.domainId,
      donations: config.donations,
      expeditedCheckoutEnabled: config.expeditedCheckoutEnabled,
      walletsEnabled: config.walletsEnabled,
      checkoutText: config.checkoutText,
      // The mounted launcher publishes its imperative open/close handle here so
      // the always-present openDonation/closeDonation API methods can drive the
      // single dialog. Cleared on unmount (registerControls(null)).
      registerControls: (controls: DonationControls | null) => { donationControls = controls; },
    }), mountPoint('donations'));
  };

  // ensureDonationsMounted loads the domain's donation config and mounts the
  // launcher once, so openDonation and the declarative [data-ezoic-donate]
  // delegation work without any setup call. A no-op when donations are absent or
  // the config fails to load (donationControls stays null; the caller warns).
  // Returns the failure's tenant flags — the local-dev guidance gate
  // distinguishes a tenant 404 from a down backend.
  const ensureDonationsMounted = async (): Promise<{ domainNotFound: boolean; domainNotSetup: boolean }> => {
    const { config, domainNotFound, domainNotSetup } = await fetchDonationsDetailed();
    if (!config) {
      return { domainNotFound, domainNotSetup };
    }
    mountDonations(config);
    return { domainNotFound: false, domainNotSetup: false };
  };

  // openDonation opens the donation dialog imperatively and is the single
  // donation entry point. It is order-independent: if the launcher is not
  // mounted yet it self-mounts first, so it works without any setup call. opts
  // carries per-open checkout callbacks (onSuccess/onCancel/onError). On a domain
  // with no donations the launcher never mounts, so this logs a warning and
  // returns rather than throwing.
  const openDonationFlow = async (opts?: OpenDonationOptions): Promise<void> => {
    prewarm();
    await whenBodyReady();
    if (tokenLandingOverlayOpen()) {
      logWarn('suppressing donation mount while the account-link landing overlay is open');
      return;
    }
    let tenantFailure = { domainNotFound: false, domainNotSetup: false };
    if (!donationControls) {
      tenantFailure = await ensureDonationsMounted();
    }
    if (!donationControls) {
      // Local-dev guidance only when the backend answered a tenant 404 on
      // a local origin — a network/5xx failure is not a tenant problem and
      // gets the generic warn.
      if (tenantFailure.domainNotFound && isLocalHostname(window.location.hostname)) {
        mountLocalDev(opts, tenantFailure.domainNotSetup);
        return;
      }
      logWarn('openDonation found no donations configured for this domain');
      return;
    }
    donationControls.open(opts);
  };

  const openDonation = (opts?: OpenDonationOptions): Promise<void> => runModal(() => openDonationFlow(opts));

  // closeDonation closes the donation dialog if it is open; a no-op otherwise.
  const closeDonation = (): void => {
    donationControls?.close();
  };

  // showDonations is a deprecated no-op, kept only so existing embeds that still
  // call it don't throw. Donations open via openDonation() or a declarative
  // [data-ezoic-donate] element (both self-mount), so there is nothing to set up
  // here. The legacy options bag is accepted and ignored.
  const showDonations = async (_opts?: CheckoutCallbacks): Promise<void> => {
    // Deprecated no-op retained for back-compat; warn so publishers still calling
    // it learn why nothing happens and switch to openDonation().
    logWarn('showDonations is deprecated and does nothing; use openDonation() or a [data-ezoic-donate] element');
  };

  // openAccountPortal opens the subscriber portal (subscriber.ezoic.com, or
  // subscriber.open.video on Open Video pages) in a
  // new tab so a logged-in member can manage payment methods, view receipts, and
  // cancel subscriptions. For an ezAuth-verified member it hands off seamlessly —
  // the tab lands already signed in via a one-click login URL, no email
  // round-trip. Otherwise (BYO/guest, or any failure) the tab shows the bare
  // portal, which runs its own sign-in: a soft content session is deliberately
  // never bridged into the verified portal scope. Publishers wire this to a
  // "Manage subscription" link for logged-in members.
  const openAccountPortal = (): void => {
    if (!openSubscriberPortal()) {
      logWarn('openAccountPortal was blocked by the browser (popup blocker?)');
    }
  };

  // hide unmounts every surface. Unmounting the paywall runs its cleanup effect,
  // which restores page scrolling.
  const hide = (): void => {
    mountedCheckout?.destroy();
    modalPaywallOpen = false;
    for (const name of ['paywall', 'donations', 'login', 'return'] as const) {
      const point = mountPoints[name];
      if (point) render(null, point);
    }
  };

  // initialize registers the publisher's BYO auth adapter. Publisher-driven and
  // safe to call repeatedly; a later valid call replaces the adapter.
  const initialize = (config: { auth: AuthAdapter }): void => {
    registerAdapter(config?.auth);
  };

  // reopenParkedSelection remounts a parked selection (BYO login park or trial
  // 3DS park). 'busy': a mounted checkout is active, so the resume must not
  // stack a modal on it. 'unavailable': the selection resolves to nothing to
  // sell, or carries no usable selection. trial threads the 3DS-return resume
  // into the remounted flow.
  const reopenParkedSelection = async (intent: PendingIntent, trial?: ReturnedTrialSetup): Promise<'reopened' | 'busy' | 'unavailable'> => {
    if (hasMountedCheckout()) {
      return 'busy';
    }
    // 'checkout' is the openCheckout path; 'item' is its legacy tag (still
    // resumed so a pre-deploy parked intent isn't dropped).
    if ((intent.kind === 'checkout' || intent.kind === 'item') && intent.price) {
      // A price that resolves to nothing to sell makes openCheckout a no-op
      // mount; report it, mirroring the product-kind guard below. A null
      // config (load failure) still proceeds — openCheckout retries the fetch
      // and surfaces the error step if it fails again. The fetch is memoized,
      // so openCheckout's own load is a cache hit.
      const { config: priceConfig } = await fetchPriceConfigDetailed(intent.price, intent.promo?.code);
      if (hasMountedCheckout()) {
        return 'busy';
      }
      if (priceConfig && !priceConfig.product) {
        logWarn('could not reload price config to resume checkout', intent.price);
        return 'unavailable';
      }
      // A tenant 403/404 makes openCheckout a no-op mount too; it still counts
      // as reopened — a disabled or unrecognized domain will never resume, so
      // keeping the park would be dead weight.
      await runModal(() => openCheckoutFlow({ price: intent.price!, item: intent.item, dismissible: intent.dismissible, resumed: true, campaignPromo: intent.promo, trialSetupResume: trial }), parkedDialogAnchor(intent.anchor));
      return 'reopened';
    }
    if (intent.kind === 'product' && intent.product) {
      const config = await fetchProductConfig(intent.product, intent.promo?.code);
      if (hasMountedCheckout()) {
        return 'busy';
      }
      if (!config?.product) {
        logWarn('could not reload product config to resume checkout', intent.product);
        return 'unavailable';
      }
      setDialogAnchor(parkedDialogAnchor(intent.anchor));
      mountPaywall(config, { product: intent.product, item: intent.item, dismissible: intent.dismissible !== false, resumePriceExternalId: intent.priceExternalId, resumeTrial: trial ? true : undefined, campaignPromo: intent.promo ?? null, resumed: true, trialSetupResume: trial });
      return 'reopened';
    }
    logWarn('ignoring parked checkout with no usable selection', intent);
    return 'unavailable';
  };

  // resumePendingIntent re-opens a BYO checkout the visitor left to log in. It
  // only fires for a logged-in visitor on the page the checkout started on.
  // Clear the parked intent AFTER remount — not before the config reload —
  // so a login that unloads this document mid-fetch still resumes on the next
  // load. A selection with nothing to sell stays parked. A logged-in visitor's
  // gate is skipped, so the re-opened surface advances straight to payment.
  const resumePendingIntent = async (): Promise<void> => {
    await whenBodyReady();
    const intent = getPendingIntent();
    // A trial card-check park belongs to resumeTrialSetupReturn.
    if (!intent || intent.trialSetup || intent.originPath !== window.location.pathname) {
      return;
    }
    const outcome = await reopenParkedSelection(intent);
    if (outcome === 'unavailable') {
      return;
    }
    if (outcome === 'busy') {
      logWarn('skipping checkout resume while a mounted checkout is active');
    }
    clearPendingIntent();
  };

  // resumeSecureRedirect re-opens a checkout that was parked in the ezsm_resume
  // URL param when the visitor accepted the secure-checkout redirect on an
  // insecure (http) page. Runs once per load, after bootstrapDone so a restored
  // session lets the resumed flow skip the identity gate. The param is stripped
  // before dispatch (single-shot; a reload does not reopen checkout), and a
  // malformed param is stripped and ignored rather than breaking page load.
  const resumeSecureRedirect = async (): Promise<void> => {
    await whenBodyReady();
    if (!new URLSearchParams(window.location.search).has(RESUME_PARAM)) {
      return;
    }
    const payload = parseResumeParam(window.location.search);
    stripResumeParam();
    if (hasMountedCheckout()) {
      logWarn('skipping secure checkout resume while a mounted checkout is active');
      return;
    }
    if (!payload) {
      return;
    }
    // The param should only ever be seen on the https page it targeted. If the
    // site bounced us back to http (redirect loop, clicked-through cert), never
    // auto-redirect a second time — strip, warn, render nothing.
    if (!isSecureContext()) {
      logWarn('secure checkout resume arrived on an insecure page; ignoring (https redirect loop?)');
      return;
    }
    // 'checkout' is the openCheckout path; 'item' is its legacy tag.
    if ((payload.kind === 'checkout' || payload.kind === 'item') && payload.price) {
      await runModal(() => openCheckoutFlow({ price: payload.price!, item: payload.item, dismissible: payload.dismissible, resumed: true, campaignPromo: payload.promo }));
      return;
    }
    if (payload.kind === 'product' && payload.product) {
      // Park the payload BEFORE fetching: it merges into every matching
      // mountPaywall until dismissed/completed (see mountPaywall), so the
      // resumed preselection survives a publisher pop-on-load mount landing
      // after the resume's own.
      pendingSecureResume = payload;
      const config = await fetchProductConfig(payload.product, payload.promo?.code);
      if (hasMountedCheckout()) {
        pendingSecureResume = null;
        logWarn('skipping secure checkout resume while a mounted checkout is active');
        return;
      }
      if (!config?.product) {
        pendingSecureResume = null;
        logWarn('could not reload product config to resume secure checkout', payload.product);
        return;
      }
      setDialogAnchor(null);
      mountPaywall(config, { product: payload.product, dismissible: payload.dismissible !== false, campaignPromo: payload.promo ?? null, resumed: true });
      return;
    }
    // Well-formed envelope but missing the field its kind requires (tampered
    // or buggy param) — already stripped; say so rather than vanishing silently.
    logWarn('ignoring secure checkout resume param with no usable selection', payload);
  };
  // Queued on bootstrapDone so the resumed flow sees the restored session.
  // Ordering vs publisher cmd callbacks doesn't matter: the parked payload
  // merges into every matching mountPaywall for the load (see mountPaywall).
  void bootstrapDone.then(resumeSecureRedirect).catch((err) => logError('secure checkout resume failed', err));

  // resumeTrialSetupReturn finishes a trial whose card check left the page for
  // an issuer's 3DS redirect. Stripe returns with setup_intent/redirect_status
  // params; a succeeded return matching the trial park (CheckoutFlow parks it
  // around confirmSetup) remounts the flow, which confirms the trial on that
  // SetupIntent. The resume stays load-scoped (pendingTrialResume) until a
  // flow takes it. Runs once per load after bootstrapDone so a restored session
  // lets the resumed flow skip the email step. Only our own return consumes the
  // park: a load without it (another tab, a reload mid-check) leaves the park
  // to its TTL, since clearing it there would bring the redirect loop back. A
  // BYO login park (no trialSetup) is never touched.
  const resumeTrialSetupReturn = async (): Promise<void> => {
    await whenBodyReady();
    const setupReturn = readSetupIntentReturn(window.location.search);
    const intent = getPendingIntent();
    const trialSetup = intent?.trialSetup;
    if (!intent || !trialSetup || setupReturn?.setupIntentId !== trialSetup.setupIntentId) {
      return;
    }
    // Strip only our own SetupIntent's params: the host page may run its own
    // Stripe integration that reads setup_intent on return.
    stripSetupIntentReturn();
    // A failed card check or another page's park is settled: nothing resumes.
    if (setupReturn.redirectStatus !== 'succeeded' || intent.originPath !== window.location.pathname) {
      clearPendingIntent();
      return;
    }
    // Parked before the config fetch so a publisher pop-on-load mount landing
    // after it still carries the resume (see mountPaywall). The client secret
    // rides in memory only: it lets a buyer with no credential confirm.
    const returned: ReturnedTrialSetup = { ...trialSetup, setupIntentClientSecret: setupReturn.clientSecret ?? undefined };
    pendingTrialResume = { intent, setup: returned };
    const outcome = await reopenParkedSelection(intent, returned);
    if (outcome === 'busy') {
      logWarn('skipping trial 3DS resume while a mounted checkout is active');
    }
    // The stored park goes once the flow is back on screen (the mounted flow
    // or a later mount on this load owns it now), and only if a newer card
    // check hasn't replaced it meanwhile.
    if (outcome !== 'reopened' || getPendingIntent()?.trialSetup?.setupIntentId !== trialSetup.setupIntentId) {
      return;
    }
    clearPendingIntent();
  };
  void bootstrapDone.then(resumeTrialSetupReturn).catch((err) => logError('trial 3DS resume failed', err));

  // openEzAuthUrlToken opens the standalone login surface when the landing URL
  // carries an ezAuth account-link token (?ezauth_reset_token= /
  // ?ezauth_verify_token= from sol's emailed links). ezAuth's mounted form reads
  // the same param and shows its reset/verify screen — but only once mounted,
  // and nothing mounts it on a plain page load. BYO is skipped: the publisher
  // owns auth there, and login() would navigate away uninvited. Dismissing the
  // overlay is "not now": the token is stripped so the overlay does not
  // reappear on every reload of the emailed link (it is unspent and still
  // usable by re-clicking the email). On a completed reset/verify ezAuth has
  // already stripped the param, so that close is a no-op here.
  const openEzAuthUrlToken = async (): Promise<void> => {
    if (ezAuthUrlTokenKind() === null) {
      return;
    }
    const config = await getSiteConfig();
    if (config?.authMode === 'byo') {
      return;
    }
    setDialogAnchor(null);
    await loginFlow({ onClosed: stripEzAuthUrlToken });
  };
  void bootstrapDone.then(openEzAuthUrlToken).catch((err) => logError('ezAuth url-token landing failed', err));

  // authChanged re-resolves identity through the registered adapter and refreshes
  // the SM content session, then fires access:change. Runs are serialized so
  // rapid login/logout signals do not interleave.
  let authChangePending = Promise.resolve();
  const runAuthChanged = async (): Promise<void> => {
    const adapter = getAdapter();
    if (!adapter) {
      return;
    }
    // Begun before the adapter awaits: a sign-out or new sign-in during this
    // run keeps it from adopting, clearing or announcing a session.
    const operation = beginSessionRequest();
    // Resolve the publisher-asserted identity, token-first: a registered
    // getIdentityToken yields the SM-minted identity token (the plaintext email
    // stays off the page); otherwise fall back to the plaintext email.
    let credential: { identityToken: string } | { email: string } | null = null;
    try {
      if (typeof adapter.getIdentityToken === 'function') {
        const token = await adapter.getIdentityToken();
        const trimmedToken = typeof token === 'string' ? token.trim() : '';
        if (trimmedToken) {
          credential = { identityToken: trimmedToken };
        }
      }
    } catch (err) {
      // Same contract as a broken getUserEmail: never throw out of authChanged,
      // never clobber the current session on an adapter failure.
      logError('auth adapter getIdentityToken failed', err);
      return;
    }
    if (!credential && typeof adapter.getUserEmail === 'function') {
      let email: string | null = null;
      try {
        email = await adapter.getUserEmail();
      } catch (err) {
        logError('auth adapter getUserEmail failed', err);
        return;
      }
      const trimmed = typeof email === 'string' ? email.trim() : '';
      if (trimmed) {
        credential = { email: trimmed };
      }
    }
    let authenticated = false;
    if (credential) {
      // Only the session write waits for restoration (magic link, pending
      // checkout): a restored session landing after it still adopts while
      // someone is signed in and would replace the publisher's identity. The
      // guard above keeps master's ordering. bootstrapDone never rejects.
      await bootstrapDone;
      try {
        authenticated = await establishSession(credential, undefined, operation);
      } catch (err) {
        logError('establish session failed', err);
      }
      // A rejected identity token is most often staleness: the page held a
      // token past its 1-hour TTL. Re-pull once — the adapter contract is
      // mint-on-demand, so a publisher still logged in yields a fresh token —
      // before concluding the visitor is anonymous and dropping their session.
      if (!authenticated && operation.isCurrent() && 'identityToken' in credential && typeof adapter.getIdentityToken === 'function') {
        try {
          const fresh = await adapter.getIdentityToken();
          const trimmedFresh = typeof fresh === 'string' ? fresh.trim() : '';
          if (trimmedFresh && trimmedFresh !== credential.identityToken) {
            credential = { identityToken: trimmedFresh };
            authenticated = await establishSession(credential, undefined, operation);
          }
        } catch (err) {
          logError('identity token refresh failed', err);
        }
      }
    }
    // Overtaken by a sign-out or a new sign-in: that change owns the session
    // and already announced itself. (A sign-in that landed while someone was
    // signed in still authenticates; see SessionRequest.adopt.)
    if (!authenticated && !operation.isCurrent()) {
      return;
    }
    // A logged-out or unrecognized visitor must not keep a stale session. With
    // none stored there is nothing to sign out of: clearing would cancel a
    // pending checkout completion that is about to establish this buyer (a
    // first-time BYO buyer, or a guest the publisher reports as logged out).
    if (!authenticated && hasSession()) {
      clearSession();
    }
    emitAccessChange();
    // A just-logged-in visitor resumes any checkout they left to log in. Gate on
    // being logged in at the publisher (a resolved credential), NOT on
    // establishSession succeeding: a first-time BYO buyer is authenticated by
    // the publisher but is not yet a known SM visitor, so establish returns
    // false (no session) — and they are exactly who must be taken to payment.
    // Checkout-start carries the credential directly, so no prior SM session is
    // required.
    if (credential) {
      await resumePendingIntent();
      return;
    }
    // Logged-out signal on the page a checkout was started: the visitor left to
    // log in and came back without authenticating (cancelled or failed), so drop
    // the stale intent rather than letting a later login on this page silently
    // re-open it. Scoped to the origin path so an intent parked for another page
    // survives.
    const intent = getPendingIntent();
    if (intent && !intent.trialSetup && intent.originPath === window.location.pathname) {
      clearPendingIntent();
    }
  };
  const authChanged = (): Promise<void> => {
    // runAuthChanged never rejects; the second handler keeps the chain alive so a
    // future regression that lets it throw can't permanently poison serialization.
    authChangePending = authChangePending.then(runAuthChanged, runAuthChanged);
    return authChangePending;
  };

  const on = (event: 'access:change', handler: AccessChangeHandler): Unsubscribe => {
    if (event !== 'access:change') {
      logError('on: unknown event; only "access:change" is supported', event);
      return () => {};
    }
    return onAccessChange(handler);
  };

  const off = (event: 'access:change', handler: AccessChangeHandler): void => {
    if (event !== 'access:change') {
      logError('off: unknown event; only "access:change" is supported', event);
      return;
    }
    offAccessChange(handler);
  };

  const api: EzSubscriptionsApi = {
    cmd: [],
    ready: true,
    initialize,
    authChanged,
    on,
    off,
    disableAds,
    allowAds,
    hasAccess,
    hasPurchased,
    getProducts,
    getPurchases,
    showPaywall,
    openCheckout,
    mountCheckout,
    openDonation,
    closeDonation,
    showDonations,
    login,
    logout,
    getSessionToken,
    getSandboxPreviewToken,
    openAccountPortal,
    trackEvent: trackCustomEvent,
    hide,
  };
  return api;
}

interface InlineCheckout {
  host: HTMLElement;
  point: HTMLElement;
  successUrl?: string;
  isActive: () => boolean;
}

function inlinePresentation(children: ComponentChildren, inline: InlineCheckout) {
  return h(CheckoutPresentationProvider, { value: { inline: true, successUrl: inline.successUrl, isActive: inline.isActive }, children });
}

function resolveCheckoutTarget(target: HTMLElement | string): HTMLElement {
  if (typeof target === 'string') {
    let matches: NodeListOf<Element>;
    try {
      matches = document.querySelectorAll(target);
    } catch {
      throw mountCheckoutError('invalid_target', 'mountCheckout target must be a valid selector.');
    }
    if (matches.length !== 1) {
      throw mountCheckoutError('invalid_target', 'mountCheckout target must match exactly one connected element.');
    }
    target = matches[0] as HTMLElement;
  }
  if (!(target instanceof HTMLElement) || !target.isConnected || target.getRootNode() !== document) {
    throw mountCheckoutError('invalid_target', 'mountCheckout target must be a connected HTMLElement.');
  }
  if (/^(AREA|BASE|BR|COL|EMBED|HR|IMG|INPUT|LINK|META|PARAM|SOURCE|TRACK|WBR)$/.test(target.tagName)) {
    throw mountCheckoutError('invalid_target', 'mountCheckout target must support child elements.');
  }
  return target;
}

function mountCheckoutError(code: MountCheckoutError['code'], message: string): MountCheckoutError {
  return Object.assign(new Error(message), { code });
}

function resolveSuccessUrl(value?: string): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  let url: URL;
  try {
    url = new URL(value, window.location.href);
  } catch {
    throw mountCheckoutError('invalid_success_url', 'mountCheckout successUrl must be a relative or same-origin HTTP(S) URL.');
  }
  if (!['http:', 'https:'].includes(url.protocol) || url.origin !== window.location.origin || url.username || url.password) {
    throw mountCheckoutError('invalid_success_url', 'mountCheckout successUrl must be a relative or same-origin HTTP(S) URL.');
  }
  return url.href;
}

function paywallProductHandle(opts?: { product?: string; offer?: string }): string {
  return (opts?.product ?? opts?.offer ?? '').trim();
}

function runReadyCallback(api: EzSubscriptionsApi, cb: EzSubscriptionsReadyCallback): void {
  Promise.resolve().then(() => cb(api)).catch((err) => logError('ready callback failed', err));
}

// The widget renders into a single shadow root so its styles never leak into the
// publisher page. Each surface (paywall, donations, login) gets its own
// container so they mount and unmount independently.
let shadowRoot: ShadowRoot | null = null;
let shadowHost: HTMLElement | null = null;
const mountPoints: Partial<Record<'paywall' | 'donations' | 'login' | 'return', HTMLElement>> = {};

// The mounted DonationLauncher's imperative open/close handle, published via its
// registerControls prop and cleared on unmount. null means the dialog is not
// mounted (never shown, no donations configured, or hidden).
let donationControls: DonationControls | null = null;

// A token-landing login overlay on screen (?ezauth_reset_token= /
// ?ezauth_verify_token= from sol's emailed links, auto-opened by
// openEzAuthUrlToken) takes precedence: any other surface mounting now would
// cover the reset/verify form the visitor came for. Stateless on purpose —
// every unmount path (onClose, hide(), remount) empties the login point, so
// suppression lifts itself; there is no flag to leave stuck suppressing
// paywalls. The token condition scopes this to token landings: a nav-click
// login on a normal page suppresses nothing.
function tokenLandingOverlayOpen(): boolean {
  return ezAuthUrlTokenKind() !== null && mountPoints['login']?.firstChild != null;
}

function ensureShadowRoot(): ShadowRoot {
  // No isConnected re-guard: nothing detaches #ezoic-sm, and a full navigation
  // re-executes this module with null state anyway.
  if (shadowRoot) return shadowRoot;
  const host = document.createElement('div');
  host.id = 'ezoic-sm';
  // The host is a sibling of the publisher's own DOM, not a descendant of
  // whatever trigger opened a surface. Publisher pages commonly run their own
  // document-level "click outside this menu/nav closes it" listeners; without
  // this, a click inside a mounted surface (e.g. the dialog's own close
  // button) bubbles out, reads as an "outside" click to that listener, and can
  // collapse a mobile nav the trigger lives in — hiding the trigger once the
  // dialog closes. Stopped here, at the point it would otherwise escape into
  // the publisher's document, so it covers every surface after it has already
  // finished bubbling through the shadow tree.
  host.addEventListener('click', (event: MouseEvent) => event.stopPropagation());
  document.body.appendChild(host);
  shadowHost = host;
  shadowRoot = host.attachShadow({ mode: 'open' });
  // Direction comes from the resolved catalog locale, which applyVisitorLanguage
  // sets from config responses that all run before this host exists on first
  // mount — apply it now so an ar/he page's first surface renders RTL.
  applyHostDirection(host);
  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  shadowRoot.appendChild(styleEl);
  return shadowRoot;
}

// shadowHostElement returns the theme target: the shadow host (#ezoic-sm) that
// carries data-sm-template/mode and the --sm-*-theme accent layer. Public tokens
// resolve theme → classic default, and a publisher's --sm-TOKEN on #ezoic-sm
// outranks both (see tokens.ts / docs/architecture/paywall-theming.md).
// A single shared host means the last-mounted surface's theme is live.
function shadowHostElement(): HTMLElement {
  ensureShadowRoot();
  return shadowHost as HTMLElement;
}

function mountPoint(name: 'paywall' | 'donations' | 'login' | 'return'): HTMLElement {
  const existing = mountPoints[name];
  if (existing) return existing;
  const container = document.createElement('div');
  // sm-root carries the base typography (styles.css): on an inner element the
  // page can't override it via rules matching the light-DOM host, unlike :host.
  container.className = 'sm-root';
  ensureShadowRoot().appendChild(container);
  mountPoints[name] = container;
  return container;
}
